import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import useScreenLock from "~/hooks/modal";
import { IFormItem } from "~/store/slices/formSlice";
import { getAmount } from "../InvoiceForm/helpers";
import Text from "~/components/Text";
import Gap from "~/components/Gap";
import StyledInput from "~/components/StyledInput";
import { fmPrice } from "~/utils/amount";
import Button from "~/components/Button";
import CloseButton from "~/components/CloseButton";

// Animation variants
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const sheetVariants = {
  hidden: { y: "100%" },
  visible: { y: 0 },
  exit: { y: "100%" },
};

const Backdrop = styled(motion.div)`
  background-color: #3939394b;
  position: fixed;
  z-index: 7001;
  top: 0;
  min-height: 100dvh;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const SheetContainer = styled(motion.div)`
  border-top-right-radius: 28px !important;
  border-top-left-radius: 28px !important;
  background: white;
`;

const SheetContent = styled.div`
  padding: 16px;
`;

const SheetHeader = styled.div`
  border-bottom: 1px solid #e8e7ee;
  padding: 12px;

  .close-btn {
    top: 10px;
    left: 16px;
  }
`;

const SheetFooter = styled.div`
  border-top: 1px solid #e8e7ee;
  padding: 16px;
`;

interface IItemSheetProps {
  currency: string;
  header: string;
  isOpen: boolean;
  onClose(): void;
  data: Partial<IFormItem>;
  onSave(newData: Partial<IFormItem>): void;
  editIdx: number | null;
  onRemoveItem(): void;
}

const ItemSheet = ({
  currency,
  header,
  isOpen,
  data,
  onClose,
  onSave,
  editIdx,
  onRemoveItem,
}: IItemSheetProps) => {
  const [itemData, setItemData] = useState<Partial<IFormItem>>({
    sku: "",
    description: "",
    quantity: "1",
    price: "0",
  });

  useEffect(() => {
    if (editIdx !== null) {
      setItemData({ ...data });
    } else {
      setItemData({ sku: "", description: "", quantity: "1", price: "0" });
    }
  }, [isOpen, editIdx]);

  useScreenLock(isOpen);

  function saveData() {
    onSave(itemData);
    onClose();
  }

  function onChangeEntityField(newVal: Partial<IFormItem>) {
    setItemData({ ...itemData, ...newVal });
  }

  const amount = getAmount(itemData as IFormItem);

  return (
    <AnimatePresence>
      {isOpen && (
        <Backdrop
          variants={backdropVariants}
          initial="hidden"
          animate={isOpen ? "visible" : "hidden"}
          exit="exit"
        >
          <SheetContainer
            variants={sheetVariants}
            initial="hidden"
            animate={isOpen ? "visible" : "hidden"}
            exit="exit"
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <SheetHeader className="pos-r">
              <CloseButton className="pos-a close-btn" onClose={onClose} />
              <Text align="center" size="sm" className="f-med">
                {header}
              </Text>
            </SheetHeader>
            <SheetContent>
              <Text size="xs">
                Description <span className="danger">*</span>
              </Text>
              <Gap t={4} />
              <StyledInput
                className={`${!itemData["description"] && "error-field"}`}
                type="text"
                value={itemData["description"]}
                onChange={(e) =>
                  onChangeEntityField({ description: e.target.value })
                }
              />
              <Gap t={16} />
              <div className="full-width center">
                <div className="full-width">
                  <Text size="xs">Quantity</Text>
                  <Gap t={4} />
                  <StyledInput
                    className={`${!itemData["quantity"] && "error-field"}`}
                    type="text"
                    value={itemData["quantity"]}
                    onChange={(e) =>
                      onChangeEntityField({ quantity: e.target.value })
                    }
                  />
                </div>
                <Gap r={16} />
                <div className="full-width">
                  <Text size="xs">Unit Price</Text>
                  <Gap t={4} />
                  <StyledInput
                    className={`${!itemData["price"] && "error-field"}`}
                    type="text"
                    value={itemData["price"]}
                    onChange={(e) =>
                      onChangeEntityField({ price: e.target.value })
                    }
                  />
                </div>
              </div>

              {amount ? (
                <>
                  <Gap t={12} />
                  <Text size="xs" variant="neutral">
                    = {fmPrice(currency, amount)} ({itemData.quantity} X{" "}
                    {fmPrice(currency, itemData.price ?? "0")})
                  </Text>
                </>
              ) : null}
            </SheetContent>

            <SheetFooter className="spread">
              <div className="align-center">
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Gap r={8} />
                {editIdx !== null && (
                  <Button variant="danger" onClick={onRemoveItem}>
                    Remove
                  </Button>
                )}
              </div>

              <Button
                onClick={saveData}
                style={{
                  minWidth: "88px",
                }}
              >
                Save
              </Button>
            </SheetFooter>
          </SheetContainer>
        </Backdrop>
      )}
    </AnimatePresence>
  );
};

export default ItemSheet;
